import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, Loader2, Plus, UserRound, X } from "lucide-react";
import { useState } from "react";

import { createResident, listResidents, type ResidentPayload, type ResidentSummary } from "@/api/residents";
import { ResidentCreateDialog } from "@/components/trust_suite/ResidentCreateDialog";
import { residentListMeta } from "@/components/trust_suite/residentDisplay";
import {
  emptyResidentCreateForm,
  residentCreateFormFromPayload,
  residentCreateFormHasIdentity,
  residentCreatePayloadFromForm,
  type ResidentCreateFormState,
} from "@/components/trust_suite/residentLinkDefaults";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type ResidentLinkCardProps = {
  municipalityId: number;
  residentId: number | null;
  resident: ResidentSummary | null | undefined;
  updating?: boolean;
  onLink: (residentId: number | null) => void;
  onViewResident?: (residentId: number) => void;
  createDefaults?: ResidentPayload | null;
  createDialogTitle?: string;
  createDialogDescription?: string;
};

const UNLINKED = "unlinked";

export function ResidentLinkCard({
  municipalityId,
  residentId,
  resident,
  updating = false,
  onLink,
  onViewResident,
  createDefaults = null,
  createDialogTitle = "Create resident",
  createDialogDescription = "Create a resident profile and link it to this record.",
}: ResidentLinkCardProps) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<ResidentCreateFormState>(emptyResidentCreateForm());

  const { data, isLoading } = useQuery({
    queryKey: ["residents", municipalityId, "link-picker"],
    queryFn: () => listResidents(municipalityId, { search: "" }),
    enabled: residentId == null,
  });

  const createMutation = useMutation({
    mutationFn: (payload: ResidentPayload) => createResident(municipalityId, payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["residents", municipalityId] });
      onLink(created.id);
      setCreateOpen(false);
      setForm(emptyResidentCreateForm());
    },
  });

  const options = data?.residents ?? [];

  const openCreateDialog = () => {
    setForm(createDefaults ? residentCreateFormFromPayload(createDefaults) : emptyResidentCreateForm());
    setCreateOpen(true);
  };

  if (residentId != null && resident) {
    return (
      <Card data-testid="resident-link-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Resident profile</CardTitle>
          <CardDescription>Linked constituent CRM record</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2 text-sm">
            <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="font-medium">{resident.display_name}</p>
              {residentListMeta(resident) ? (
                <p className="text-muted-foreground">{residentListMeta(resident)}</p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {onViewResident ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onViewResident(resident.id)}
              >
                View profile
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={updating}
              onClick={() => onLink(null)}
            >
              <X className="mr-2 h-4 w-4" />
              Unlink
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="resident-link-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Resident profile</CardTitle>
          <CardDescription>Link or create a resident CRM record for this ticket</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading residents…
            </div>
          ) : (
            <Select
              value={UNLINKED}
              onValueChange={(value) => {
                if (value !== UNLINKED) {
                  onLink(Number(value));
                }
              }}
              disabled={updating || createMutation.isPending}
            >
              <SelectTrigger aria-label="Link existing resident">
                <SelectValue placeholder="Select a resident" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNLINKED} disabled>
                  Link existing resident…
                </SelectItem>
                {options.map((option) => (
                  <SelectItem key={option.id} value={String(option.id)}>
                    {option.display_name}
                    {option.email ? ` (${option.email})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-full"
            disabled={updating || createMutation.isPending}
            onClick={openCreateDialog}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create & link resident
          </Button>

          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link2 className="h-3.5 w-3.5" />
            {createDefaults
              ? "Prefills from this ticket's contact info when available."
              : "Create a resident profile and link it to this ticket."}
          </p>
        </CardContent>
      </Card>

      <ResidentCreateDialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) setForm(emptyResidentCreateForm());
        }}
        title={createDialogTitle}
        description={createDialogDescription}
        displayName={form.displayName}
        onDisplayNameChange={(value) => setForm((current) => ({ ...current, displayName: value }))}
        email={form.email}
        onEmailChange={(value) => setForm((current) => ({ ...current, email: value }))}
        phone={form.phone}
        onPhoneChange={(value) => setForm((current) => ({ ...current, phone: value }))}
        addressLine1={form.addressLine1}
        onAddressLine1Change={(value) => setForm((current) => ({ ...current, addressLine1: value }))}
        city={form.city}
        onCityChange={(value) => setForm((current) => ({ ...current, city: value }))}
        state={form.state}
        onStateChange={(value) => setForm((current) => ({ ...current, state: value }))}
        postalCode={form.postalCode}
        onPostalCodeChange={(value) => setForm((current) => ({ ...current, postalCode: value }))}
        saving={createMutation.isPending}
        submitLabel="Create & link"
        onSubmit={() => {
          if (!residentCreateFormHasIdentity(form)) return;
          createMutation.mutate(residentCreatePayloadFromForm(form));
        }}
      />
    </>
  );
}
